import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { SecondaryButton } from "@/components/Buttons/SecondaryButton";
import { Card } from "@/components/Card/Card";
import { Spacing } from "@/components/Spacing/Spacing";
import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { createPortal } from "react-dom";
import { IconX } from "@tabler/icons-react";
import { CapacityContext } from "@/lib/api/errors/ErrorHandler";
import { describeBatchUpdateError } from "@/lib/i18n/translateBackendError";

/**
 * Shared 3-option capacity decision popup. Shown when an admin's create /
 * accept would hit the guest (or waitlist) limit. Presentational only — each
 * flow (GuestsProvider create + promote, RegistrantsProvider accept) holds its
 * own state and supplies the confirm callbacks.
 *
 *  - kind "create"/"accept": offers "Auf Warteliste" (when the waitlist is
 *    enabled) + "Trotzdem als Gast" + "Abbrechen".
 *  - kind "promote": the guest is already waitlisted, so there is no waitlist
 *    choice — only "Trotzdem als Gast" (override the cap) + "Abbrechen".
 *
 * `error`: the RAW failure of the last choice (a ServerError, a thrown Error).
 * The flow keeps the popup open on a failure and hands the error here; the
 * popup translates it (the providers deliberately have no translator — R12).
 */
interface CapacityDecisionModalProps {
  context?: CapacityContext;
  kind: "create" | "accept" | "promote";
  isLoading: boolean;
  onWaitlist: () => void | Promise<void>;
  onNormal: () => void | Promise<void>;
  onCancel: () => void;
  error?: unknown;
}

export const CapacityDecisionModal: React.FC<CapacityDecisionModalProps> = ({
  context,
  kind,
  isLoading,
  onWaitlist,
  onNormal,
  onCancel,
  error,
}) => {
  const t = useTranslations("capacity-decision");
  const tErrors = useTranslations("errors");
  const errorText =
    error === undefined || error === null
      ? null
      : // Unfiltered: providers keep a ServerError, a thrown Error, a plain
        // `{message, failures}` object or a bare code — all readable here.
        describeBatchUpdateError(tErrors, error, tErrors("unknown"));
  // Track which action is in-flight so only that button spins (Cancel never does).
  const [clicked, setClicked] = useState<"waitlist" | "normal" | null>(null);

  const showWaitlist = kind !== "promote" && !!context?.globalWaitlistEnabled;
  // The waitlist is enabled but also already full — the admin can still force a
  // guest onto it (override), so we keep the button but warn in the body copy.
  const waitlistAlsoFull = showWaitlist && context?.waitlistHasRoom === false;

  const title = kind === "promote" ? t("promoteTitle") : t("title");
  const body =
    kind === "promote"
      ? t("promoteBody")
      : waitlistAlsoFull
        ? t("bodyWaitlistFull")
        : t("body");

  const modalContent = (
    <motion.div
      className="fixed left-0 top-0 bg-black/50 flex items-center justify-center sm:pr-[var(--ai-rail-push)] transition-[padding] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none h-screen w-screen z-2000 cursor-default"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        // Not while a choice is in flight: the flow keeps the popup open on a
        // failure and says why in it — closed mid-request, that answer would
        // land nowhere (or in the next guest's popup). Same as PDeleteConfirmation.
        if (e.target === e.currentTarget && !isLoading) onCancel();
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        transition={{ type: "spring", damping: 20, stiffness: 300 }}
      >
        <Card
          padding="p-6"
          width="w-[95vw] sm:max-w-125"
          rounded="rounded-3xl"
          shadow="shadow-xl"
          header={title}
          icon={IconX}
          iconActionOnClick={() => {
            if (!isLoading) onCancel();
          }}
        >
          <div className="flex flex-col items-center w-full">
            <Spacing height="h-6" />
            <p className="body-1-regular font-secondary w-full whitespace-normal">
              {body}
            </p>

            {/* Live counts vs. limits so the admin sees exactly how full it is. */}
            {context && (
              (typeof context.globalGuestLimit === "number" && context.globalGuestLimit > 0) ||
              context.globalWaitlistEnabled
            ) && (
              <>
                <Spacing height="h-5" />
                <div className="w-full rounded-2xl border outline-tertiary">
                  {typeof context.globalGuestLimit === "number" && context.globalGuestLimit > 0 && (
                    <div
                      className={`flex items-center justify-between px-4 py-2.5 ${context.globalWaitlistEnabled ? "border-b outline-tertiary" : ""}`}
                    >
                      <span className="body-3-medium font-secondary">{t("guestsLabel")}</span>
                      <span
                        className={`body-3-bold ${context.currentCount >= context.globalGuestLimit ? "font-error" : ""}`}
                      >
                        {context.currentCount} / {context.globalGuestLimit}
                      </span>
                    </div>
                  )}
                  {context.globalWaitlistEnabled && (
                    <div className="flex items-center justify-between px-4 py-2.5">
                      <span className="body-3-medium font-secondary">{t("waitlistLabel")}</span>
                      <span
                        className={`body-3-bold ${
                          context.globalWaitlistLimit && context.globalWaitlistLimit > 0 &&
                          context.waitlistedCount >= context.globalWaitlistLimit
                            ? "font-error"
                            : ""
                        }`}
                      >
                        {context.globalWaitlistLimit && context.globalWaitlistLimit > 0
                          ? `${context.waitlistedCount} / ${context.globalWaitlistLimit}`
                          : context.waitlistedCount}
                      </span>
                    </div>
                  )}
                </div>
              </>
            )}

            {errorText && (
              <>
                <Spacing height="h-5" />
                <p
                  role="alert"
                  data-testid="capacity-decision-error"
                  className="body-3-regular font-error w-full whitespace-normal"
                >
                  {errorText}
                </p>
              </>
            )}

            <Spacing height="h-8" />
            {/* Cancel is the emphasized DEFAULT — an accidental confirm must not
                waitlist or admit over the cap. The two real actions are secondary. */}
            <div className="flex flex-col w-full space-y-3">
              {showWaitlist && (
                <SecondaryButton
                  loading={isLoading && clicked === "waitlist"}
                  text={t("waitlistButton")}
                  onClick={() => {
                    setClicked("waitlist");
                    onWaitlist();
                  }}
                  width="w-full"
                  disabled={isLoading}
                  testId="capacity-decision-waitlist"
                />
              )}
              <SecondaryButton
                loading={isLoading && clicked === "normal"}
                text={t("normalButton")}
                onClick={() => {
                  setClicked("normal");
                  onNormal();
                }}
                disabled={isLoading}
                width="w-full"
              />
              <PrimaryButton
                text={t("cancelButton")}
                onClick={() => onCancel()}
                disabled={isLoading}
                width="w-full"
                testId="capacity-decision-cancel"
              />
            </div>
          </div>
        </Card>
      </motion.div>
    </motion.div>
  );

  return typeof document !== "undefined"
    ? createPortal(modalContent, document.body)
    : null;
};
